import React, { useEffect, useRef, useState } from 'react'
import {
  BackToDashboard,
  CheckoutContent,
  CheckoutCreditCardDetailsDesc,
  CheckoutCreditCardWrapper,
  CheckoutCreditTitle,
  CheckoutMainArea,
  CheckoutSeparator,
  CheckoutStyled,
  CQEssentialsLogoWrapper,
} from 'Features/checkout/views/Checkout/Checkout.styled'
import { useFrontEndEventTrackingService } from '@clickcease/cc-client-components-lib'
import { CQEssentialsLogo } from 'Assets/icons/CQEssentialsLogo'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCreditCard } from '@fortawesome/pro-light-svg-icons'
import { CheckoutPlanDetails } from 'Features/checkout/components/CheckoutPlanDetails/CheckoutPlanDetails'
import { useTranslation } from 'react-i18next'
import { CheckoutSuccess } from 'Features/checkout/components/CheckoutSuccess/CheckoutSuccess'
import { useLocation, useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { Plan } from 'Features/pricing/types/Plan'

import { useFindPlanData } from 'Features/pricing/hooks/useFindPlanData'
import { CURRENCIES, CurrenciesTypes } from 'Types/Currencies'
import { PRODUCT_TYPES, ProductTypes } from 'Types/ProductTypes'
import { CheckoutCardDetailsWrapper } from 'Features/checkout/components/CheckoutCardDeatilsWrapper/CheckoutCardDetailsWrapper'
import { CouponType } from 'Features/checkout/components/Coupon/Coupon'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useVerifyUrlCodeFromParams } from 'Features/auth/hooks/useVerifyUrlCodeFromParams'
import { useGetPartnership } from './api/useGetPartnership'
import { PlanDetails } from 'Features/pricing/types/PlanDetails'
import { Products } from 'Types/Products'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { faArrowLeft } from '@fortawesome/pro-thin-svg-icons'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'
import { useUserStore } from 'Stores/userStoreModuleFed'

interface NormalPlanState {
  planId?: number
  planName?: string
  currency: string
  amount?: number
  pricingId?: string
  allowedVisits?: number
  planType?: string
  bundlePlan?: Plan
  paidMarketingPlan?: Plan
  botMitigationPlan?: Plan
  reactivation?: boolean
  billingCycleMonths?: number
}

export const Checkout = () => {
  const { t } = useTranslation('checkout')
  const { track } = useFrontEndEventTrackingService()
  const location = useLocation()
  const { findCheckoutPlan } = useFindPlanData()
  const plans = useGetPlans()
  const { reactivationPrevPlans } = useUserStore()
  const locationState = location.state as NormalPlanState
  const DISCOUNT = 0.85
  const [couponObject, setCouponObject] = useState<CouponType | null>(null)
  const [toggleMonthlyState, setToggleMonthlyState] = useState<boolean>(false)
  const [couponValidityStatus, setCouponValidityStatus] = useState<string>('')

  const getPlanName = (planData: {
    newPlanA: Plan | undefined
    newPlanB: Plan | undefined
    product: ProductTypes
  }) => {
    const productA =
      planData?.newPlanA?.description.includes(PRODUCT_TYPES.PAID_MARKETING) &&
      !planData?.newPlanA?.description.includes(PRODUCT_TYPES.BUNDLE)
        ? PRODUCT_TYPES.PAID_MARKETING
        : PRODUCT_TYPES.BOT_ZAPPING
    return t(`${productA}_plan_${planData?.newPlanA?.planType?.toLowerCase() || ''}`)
  }

  //find plan from url params if skipped pricing page or by location state if came from pricing page
  const planData = findCheckoutPlan()
  const planId = locationState ? locationState.planId : planData?.newPlanA?.id
  const amount = locationState ? locationState.amount : planData?.newPlanA?.amount
  const planType = locationState
    ? locationState.planType
    : planData?.newPlanA?.paidMarketingPlanType
  const planName = locationState ? locationState.planName : getPlanName(planData)
  const currency = locationState
    ? (locationState.currency as CurrenciesTypes)
    : planData?.newPlanA?.currency || CURRENCIES.USD
  const allowedVisits = locationState
    ? locationState.allowedVisits
    : planData?.newPlanA?.paidMarketingAllowedVisits || planData?.newPlanA?.botZappingAllowedVisits
  const bundlePlan = locationState
    ? locationState.bundlePlan
    : planData?.newPlanA?.paidMarketingPlanType && planData?.newPlanA?.botZappingPlanType
    ? planData?.newPlanA
    : undefined
  const paidMarketingPlan = locationState ? locationState.paidMarketingPlan : planData.newPlanA
  const botMitigationPlan = locationState ? locationState.botMitigationPlan : planData.newPlanB
  const bundleDetails = [paidMarketingPlan, botMitigationPlan] as Plan[]
  const [finishedCheckout, setFinishedCheckout] = useState<boolean>(false)
  const planSelectionTrackedRef = useRef<boolean>(false)
  const { getSessionStorageItem } = useVerifyUrlCodeFromParams()
  const retrievedPartnershipValue = getSessionStorageItem('partner')
  const { data: fetchedPartnership, isFetching } = useGetPartnership(retrievedPartnershipValue)
  const navigate = useNavigate()
  const pricingEndOfTrial = usePricingEndOfTrial()
  const paymentFlow = usePaymentsFlowSplit()
  const shouldDisplayBackButton =
    paymentFlow === 'NO_CC' &&
    pricingEndOfTrial &&
    !finishedCheckout &&
    !locationState?.reactivation
  useEffect(() => {
    if (!planSelectionTrackedRef.current) {
      sendLog('planSelection', null, null)
      planSelectionTrackedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleToggleMonthly(locationState?.billingCycleMonths === 1 || false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.billingCycleMonths])

  const checkOutSuccess = (country: string, state: string | null) => {
    setFinishedCheckout(true)
    sendLog('checkout', country, state)
    if (couponObject?.valid) {
      track(['segment'], `Coupon Applied`, { couponObject })
    }
  }

  const sendLog = (
    type: 'planSelection' | 'checkout',
    country: string | null,
    state: string | null
  ) => {
    track(['dataLayer', 'segment'], type === 'planSelection' ? 'planSelection' : 'Registration', {
      ...(amount && { amount }),
      ...(planType && { planType }),
      ...(planId && { planId }),
      ...(planName && { planName }),
      ...(allowedVisits && { allowedVisits }),
      ...(currency && { currency }),
      ...(bundlePlan && { bundlePlan }),
      ...(country && { country }),
      ...(state && { state }),
    })
  }

  const getPlansIds = () => {
    if (paidMarketingPlan && botMitigationPlan) {
      return [paidMarketingPlan.id, botMitigationPlan.id]
    } else {
      return [planId || 0]
    }
  }

  const productsSelected = () => {
    const productDetails = (
      productType: string,
      planType: string,
      allowedVisits: number,
      currency: string
    ) => ({
      productType,
      planType: planType,
      allowedVisits,
      currency,
    })

    let productsList: PlanDetails[] = []

    if (planName) {
      productsList.push(
        productDetails(
          planName.includes('paidmarketing')
            ? Products.PAID_MARKETING
            : planName.includes('botzapping')
            ? Products.BOT_ZAPPING
            : '',
          planType || '',
          allowedVisits || 0,
          currency
        )
      )
    } else {
      productsList = bundleDetails?.map((planDetails) =>
        productDetails(
          planDetails?.productType,
          planDetails?.planType || '',
          planDetails?.visitsAllowed,
          planDetails?.currency
        )
      )
    }

    return productsList
  }

  const productsList = productsSelected()
  let installmentsPlanAmount = 0
  if (plans?.data && productsList && productsList.length > 0) {
    installmentsPlanAmount = productsList.reduce((total: number, selected: PlanDetails) => {
      const plan = plans.data?.find(
        (p) =>
          p.visitsAllowed === selected.allowedVisits &&
          p.planType === selected.planType &&
          p.productType === selected.productType &&
          p.billingCycleMonths === 1 &&
          p.currency === selected.currency
      )
      if (plan) {
        total += plan.amount
      }

      return total
    }, 0)
  }

  const getAmount = bundlePlan
    ? ((planData?.newPlanA?.amount || 0) + (planData?.newPlanB?.amount || 0)) * DISCOUNT
    : amount || 0
  const calculateCouponStatus = (couponObject: CouponType, toggleMonthlyState: boolean) => {
    let status = ''
    if (couponObject && couponObject.valid === false) {
      status = 'invalid'
    } else if (
      toggleMonthlyState &&
      couponObject &&
      couponObject.valid === true &&
      (couponObject.duration !== 'forever' || couponObject.couponType !== 'Percentage')
    ) {
      status = 'invalidMonthly'
    } else {
      status = 'valid'
    }
    return status
  }
  const handleCoupon = (couponObject: CouponType) => {
    if (couponObject) {
      setCouponObject(couponObject)
      const couponStatus = calculateCouponStatus(couponObject, toggleMonthlyState)
      setCouponValidityStatus(couponStatus)
    }
  }

  const handleToggleMonthly = (toggleMonthlyState: boolean) => {
    setToggleMonthlyState(toggleMonthlyState)
    if (couponObject) {
      setCouponObject(couponObject)
      const couponStatus = calculateCouponStatus(couponObject, toggleMonthlyState)
      setCouponValidityStatus(couponStatus)
    }
  }

  useEffect(() => {
    if (fetchedPartnership) {
      setCouponObject(fetchedPartnership.coupon)
      const couponStatus = calculateCouponStatus(fetchedPartnership.coupon, toggleMonthlyState)
      setCouponValidityStatus(couponStatus)
    }
  }, [fetchedPartnership, toggleMonthlyState])

  const reactivationSameProduct = () => {
    if (paidMarketingPlan && botMitigationPlan) {
      //bundle
      return (
        locationState.paidMarketingPlan?.id === reactivationPrevPlans[0] &&
        locationState.botMitigationPlan?.id === reactivationPrevPlans[1]
      )
    } else {
      //single plan
      return locationState?.planId === reactivationPrevPlans[0]
    }
  }

  return (
    <CheckoutStyled>
      <CQEssentialsLogoWrapper hasBackButton={shouldDisplayBackButton}>
        <CQEssentialsLogo width={'200px'} height={'40px'} />
        {shouldDisplayBackButton && (
          <BackToDashboard
            onClick={() => {
              navigate('/')
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft as IconProp} />
            {t('back_to_dashboard')}
          </BackToDashboard>
        )}
      </CQEssentialsLogoWrapper>
      {!finishedCheckout ? (
        <CheckoutContent>
          <CheckoutCreditCardWrapper>
            <FontAwesomeIcon icon={faCreditCard as IconProp} />
          </CheckoutCreditCardWrapper>
          <CheckoutCreditTitle>
            {t(
              locationState?.reactivation ? 'reactivate_your_account' : 'insert_credit_card_details'
            )}
          </CheckoutCreditTitle>
          <CheckoutMainArea>
            <CheckoutCardDetailsWrapper
              planIds={getPlansIds() || []}
              currency={currency}
              productsSelected={productsList}
              setFinishedCheckout={(country: string, state: string | null) =>
                checkOutSuccess(country, state)
              }
              onValidateCoupon={handleCoupon}
              onToggleMonthly={handleToggleMonthly}
              partnerTrialDays={fetchedPartnership?.trialDays}
              isFetching={isFetching}
              reactivation={locationState?.reactivation}
              reactivationSameProduct={reactivationSameProduct()}
              billingCycleMonths={locationState?.billingCycleMonths}
              partner={retrievedPartnershipValue}
            />
            {!isMobile && <CheckoutSeparator />}
            <CheckoutPlanDetails
              bundle={!!(paidMarketingPlan && botMitigationPlan)}
              planName={planName || ''}
              currency={currency}
              amount={getAmount}
              monthlyInstallmentAmount={installmentsPlanAmount}
              allowedVisits={allowedVisits}
              paidMarketingPlan={paidMarketingPlan}
              botMitigationPlan={botMitigationPlan}
              billingCycleMonths={locationState?.billingCycleMonths}
              couponObject={
                couponObject ?? {
                  amount: 0,
                  currency: '',
                  couponType: '',
                  valid: false,
                  code: '',
                  duration: '',
                }
              }
              toggleMonthlyState={toggleMonthlyState}
              partner={!!fetchedPartnership}
              reactivation={locationState?.reactivation}
              couponValidityStatus={couponValidityStatus}
            />
            {isMobile && !locationState?.reactivation && (
              <CheckoutCreditCardDetailsDesc>
                {t('you_pay_zero', { currency: getAmountWithCurrency(currency || '', undefined) })}
              </CheckoutCreditCardDetailsDesc>
            )}
          </CheckoutMainArea>
        </CheckoutContent>
      ) : (
        <CheckoutSuccess />
      )}
    </CheckoutStyled>
  )
}
